// City road map - light theme, complex irregular grid with curves.
// Cars follow actual road paths between intersections.

import { useEffect, useRef, useState } from 'react'

// ── Colours ──────────────────────────────────────────────────────────────────
const C = {
  blockFill:        '#f8f9fb',
  laneCasing:       '#f0f2f5',
  laneSurface:      '#fafbfc',
  minorCasing:      '#eaedf1',
  minorSurface:     '#f7f8fa',
  secondaryCasing:  '#e4e8ed',
  secondarySurface: '#f5f7f9',
  primaryCasing:    '#dce2e8',
  primarySurface:   '#f5f7f9',
}

// ── Road network ─────────────────────────────────────────────────────────────
const PRIMARY_H = [
  'M 0,140 L 1440,140',
  'M 0,380 L 1440,380',
  'M 0,552 L 1440,552',
]
const PRIMARY_V = [
  'M 290,0 L 290,680',
  'M 715,0 L 715,680',
  'M 1092,0 L 1092,680',
]
const SECONDARY_H = [
  'M 0,58 L 715,58',
  'M 290,262 L 1440,262',
  'M 0,462 L 1092,462',
  'M 715,628 L 1440,628',
]
const SECONDARY_V = [
  'M 128,0 L 128,380',
  'M 478,0 L 478,552',
  'M 858,140 L 858,680',
  'M 1252,0 L 1252,552',
]
const CURVED_SECONDARY = [
  'M 0,215 Q 360,178 715,208 Q 1048,238 1440,212',
  'M 290,648 Q 502,618 715,628',
  'M 1092,218 Q 1178,308 1252,380',
  'M 0,476 Q 148,468 290,462',
]
const MINOR_H = [
  'M 128,192 L 478,192',
  'M 478,320 L 858,320',
  'M 128,422 L 478,422',
  'M 290,508 L 715,508',
  'M 858,432 L 1252,432',
  'M 1092,492 L 1440,492',
  'M 0,308 L 290,308',
  'M 715,174 L 1092,174',
  'M 858,580 L 1252,580',
  'M 478,470 L 715,470',
  'M 1092,330 L 1252,330',
  'M 0,620 L 290,620',
]
const MINOR_V = [
  'M 198,140 L 198,462',
  'M 388,262 L 388,552',
  'M 610,58 L 610,380',
  'M 788,140 L 788,380',
  'M 960,140 L 960,552',
  'M 1172,380 L 1172,680',
  'M 1380,140 L 1380,552',
  'M 540,262 L 540,462',
  'M 660,380 L 660,552',
  'M 830,552 L 830,680',
]
const CURVED_MINOR = [
  'M 478,418 Q 548,402 610,380',
  'M 858,310 Q 920,340 960,380 Q 1000,420 1010,462',
  'M 715,58 Q 786,62 858,140',
  'M 1252,510 Q 1310,530 1380,552',
  'M 128,340 Q 200,330 290,320',
  'M 478,192 Q 540,210 610,262',
]
const LANE_H = [
  'M 478,152 L 610,152',
  'M 610,344 L 788,344',
  'M 388,442 L 478,442',
  'M 478,528 L 610,528',
  'M 290,596 L 388,596',
  'M 960,208 L 1092,208',
  'M 1172,462 L 1252,462',
  'M 128,340 L 198,340',
  'M 788,432 L 858,432',
  'M 715,490 L 790,490',
  'M 1092,600 L 1172,600',
  'M 0,170 L 128,170',
  'M 1320,262 L 1440,262',
]
const LANE_V = [
  'M 548,262 L 548,380',
  'M 676,58 L 676,140',
  'M 840,380 L 840,462',
  'M 1036,380 L 1036,552',
  'M 1314,380 L 1314,552',
  'M 340,140 L 340,262',
  'M 870,552 L 870,628',
  'M 200,462 L 200,552',
  'M 430,380 L 430,462',
  'M 920,58 L 920,140',
  'M 1150,0 L 1150,140',
  'M 1350,552 L 1350,628',
]

// ── Intersection nodes ────────────────────────────────────────────────────────
// Each node is a named road intersection cars can navigate between.
type Pt = { x: number; y: number }

const N: Pt[] = [
  { x: 715,  y: 380 },  // 0  - PRIMARY_H y=380 × PRIMARY_V x=715
  { x: 290,  y: 140 },  // 1  - PRIMARY_H y=140 × PRIMARY_V x=290
  { x: 1092, y: 552 },  // 2  - PRIMARY_H y=552 × PRIMARY_V x=1092
  { x: 478,  y: 262 },  // 3  - SECONDARY_H y=262 × SECONDARY_V x=478
  { x: 858,  y: 380 },  // 4  - PRIMARY_H y=380 × SECONDARY_V x=858
  { x: 715,  y: 140 },  // 5  - PRIMARY_H y=140 × PRIMARY_V x=715
  { x: 290,  y: 380 },  // 6  - PRIMARY_H y=380 × PRIMARY_V x=290
  { x: 1092, y: 140 },  // 7  - PRIMARY_H y=140 × PRIMARY_V x=1092
  { x: 715,  y: 552 },  // 8  - PRIMARY_H y=552 × PRIMARY_V x=715
  { x: 478,  y: 462 },  // 9  - SECONDARY_V x=478 × SECONDARY_H y=462
  { x: 960,  y: 262 },  // 10 - SECONDARY_H y=262 × MINOR_V x=960
  { x: 128,  y: 380 },  // 11 - PRIMARY_H y=380 × SECONDARY_V x=128
]

// ── Road graph ────────────────────────────────────────────────────────────────
// Adjacency list - only directly connected nodes (share a road segment).
const EDGES: number[][] = [
  [1, 2, 3, 4, 5, 6],    // 0
  [0, 5, 6, 7, 11],      // 1
  [0, 4, 8],             // 2
  [0, 6, 9],             // 3
  [0, 2, 5, 10],         // 4
  [0, 1, 7, 8],          // 5
  [0, 1, 3, 11],         // 6
  [1, 5, 10],            // 7
  [2, 5],                // 8
  [3, 6],                // 9
  [4, 7],                // 10
  [1, 6],                // 11
]

// ── Road paths between nodes ──────────────────────────────────────────────────
// Waypoints follow actual roads - no diagonal shortcuts.
// Format: [nodeA, nodeB, [...waypoints including A and B]]
const EDGE_PATHS: Array<[number, number, Pt[]]> = [
  // Node 0 (715,380)
  [0, 1,  [N[0], N[5], N[1]]],                         // north x=715, west y=140
  [0, 2,  [N[0], {x:1092,y:380}, N[2]]],               // east y=380, south x=1092
  [0, 3,  [N[0], {x:478,y:380}, N[3]]],                // west y=380, north x=478
  [0, 4,  [N[0], N[4]]],                               // east y=380 (direct)
  [0, 5,  [N[0], N[5]]],                               // north x=715 (direct)
  [0, 6,  [N[0], N[6]]],                               // west y=380 (direct)
  // Node 1 (290,140)
  [1, 5,  [N[1], N[5]]],                               // east y=140 (direct)
  [1, 6,  [N[1], N[6]]],                               // south x=290 (direct)
  [1, 7,  [N[1], N[7]]],                               // east y=140 (direct, full span)
  [1, 11, [N[1], {x:128,y:140}, N[11]]],               // west y=140, south x=128
  // Node 2 (1092,552)
  [2, 4,  [N[2], {x:1092,y:380}, N[4]]],               // north x=1092, west y=380
  [2, 8,  [N[2], N[8]]],                               // west y=552 (direct)
  // Node 3 (478,262)
  [3, 6,  [N[3], {x:290,y:262}, N[6]]],                // west y=262, south x=290
  [3, 9,  [N[3], N[9]]],                               // south x=478 (direct)
  // Node 4 (858,380)
  [4, 5,  [N[4], {x:858,y:140}, N[5]]],                // north x=858, west y=140
  [4, 10, [N[4], {x:960,y:380}, N[10]]],               // east y=380, north x=960
  // Node 5 (715,140)
  [5, 7,  [N[5], N[7]]],                               // east y=140 (direct)
  [5, 8,  [N[5], N[8]]],                               // south x=715 (direct, passes through N0)
  // Node 6 (290,380)
  [6, 3,  [N[6], {x:478,y:380}, N[3]]],                // east y=380, north x=478
  [6, 11, [N[6], N[11]]],                              // west y=380 (direct)
  // Node 7 (1092,140)
  [7, 10, [N[7], {x:960,y:140}, N[10]]],               // west y=140, south x=960
  // Node 9 (478,462)
  [9, 6,  [N[9], {x:290,y:462}, N[6]]],                // west y=462, north x=290
]

// Build bidirectional lookup
function buildPathLookup() {
  const map = new Map<string, Pt[]>()
  for (const [a, b, pts] of EDGE_PATHS) {
    map.set(`${a}-${b}`, pts)
    map.set(`${b}-${a}`, [...pts].reverse())
  }
  return map
}
const PATH_LOOKUP = buildPathLookup()

function getPath(from: number, to: number): Pt[] {
  return PATH_LOOKUP.get(`${from}-${to}`) ?? [N[from], N[to]]
}

function pathLength(pts: Pt[]): number {
  let len = 0
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x
    const dy = pts[i].y - pts[i - 1].y
    len += Math.sqrt(dx * dx + dy * dy)
  }
  return len
}

function positionOnPath(pts: Pt[], progress: number): Pt {
  const total = pathLength(pts)
  const target = Math.min(progress, 1) * total
  let acc = 0
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x
    const dy = pts[i].y - pts[i - 1].y
    const seg = Math.sqrt(dx * dx + dy * dy)
    if (acc + seg >= target) {
      const t = seg > 0 ? (target - acc) / seg : 0
      return { x: pts[i - 1].x + t * dx, y: pts[i - 1].y + t * dy }
    }
    acc += seg
  }
  return pts[pts.length - 1]
}

function pickRandom(arr: number[], exclude?: number): number {
  const pool = exclude !== undefined ? arr.filter(x => x !== exclude) : arr
  return pool[Math.floor(Math.random() * pool.length)]
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface Car {
  id: number
  fromNode: number
  toNode: number
  path: Pt[]
  pathLen: number
  progress: number
  speed: number   // progress per ms (set so visual px/s is consistent)
}

interface PingState {
  nodeIndex: number
  pulsing: boolean
  pulseKey: number
}

const NUM_CARS = 4
const PX_PER_MS_MIN = 0.055   // ~55 px/s
const PX_PER_MS_MAX = 0.095   // ~95 px/s

function makeCar(id: number, fromNode?: number): Car {
  const from = fromNode ?? Math.floor(Math.random() * N.length)
  const to = pickRandom(EDGES[from])
  const path = getPath(from, to)
  const len = pathLength(path)
  const pxPerMs = PX_PER_MS_MIN + Math.random() * (PX_PER_MS_MAX - PX_PER_MS_MIN)
  return {
    id,
    fromNode: from,
    toNode: to,
    path,
    pathLen: len,
    progress: Math.random(),
    speed: pxPerMs / len,
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export function HeroMap() {
  const [cars, setCars] = useState<Car[]>(() =>
    Array.from({ length: NUM_CARS }, (_, i) => makeCar(i, i * 3 % N.length)),
  )

  const [pings, setPings] = useState<PingState[]>([
    { nodeIndex: 0,  pulsing: false, pulseKey: 0 },
    { nodeIndex: 2,  pulsing: false, pulseKey: 0 },
    { nodeIndex: 7,  pulsing: false, pulseKey: 0 },
  ])

  const lastTime = useRef<number | null>(null)
  const rafRef   = useRef<number>(0)

  useEffect(() => {
    function tick(time: number) {
      const dt = lastTime.current === null ? 16 : Math.min(time - lastTime.current, 50)
      lastTime.current = time

      setCars(prev => prev.map(car => {
        const next = { ...car, progress: car.progress + car.speed * dt }

        if (next.progress >= 1) {
          const arrived = car.toNode

          // Fire pulse on any ping sitting at this node
          setPings(pp => pp.map(p =>
            p.nodeIndex === arrived
              ? { ...p, pulsing: true, pulseKey: p.pulseKey + 1 }
              : p,
          ))

          // Relocate that ping after animation completes
          setTimeout(() => {
            setPings(pp => {
              const used = pp.map(p => p.nodeIndex)
              const free = N.map((_, i) => i).filter(i => !used.includes(i))
              return pp.map(p => {
                if (p.nodeIndex !== arrived) return p
                const dest = free.length > 0
                  ? free[Math.floor(Math.random() * free.length)]
                  : pickRandom(N.map((_, i) => i), arrived)
                return { ...p, nodeIndex: dest, pulsing: false }
              })
            })
          }, 850)

          // Car picks new destination from arrived node
          const from = arrived
          const to = pickRandom(EDGES[from], car.fromNode)
          const path = getPath(from, to)
          const len = pathLength(path)
          const pxPerMs = PX_PER_MS_MIN + Math.random() * (PX_PER_MS_MAX - PX_PER_MS_MIN)
          return {
            ...next,
            fromNode: from,
            toNode: to,
            path,
            pathLen: len,
            progress: 0,
            speed: pxPerMs / len,
          }
        }

        return next
      }))

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none animate-fade-in" aria-hidden="true">
      <svg
        viewBox="0 0 1440 680"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
      >
        <defs>
          <pattern id="blocks" x="0" y="0" width="130" height="75" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="130" height="75" fill={C.blockFill} />
          </pattern>
          <radialGradient id="edgeFade" cx="50%" cy="50%" r="60%">
            <stop offset="10%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="1" />
          </radialGradient>
          <linearGradient id="centreFade" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="white" stopOpacity="0" />
            <stop offset="20%"  stopColor="white" stopOpacity="0.5" />
            <stop offset="50%"  stopColor="white" stopOpacity="0.72" />
            <stop offset="80%"  stopColor="white" stopOpacity="0.5" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>

        <rect x="0" y="0" width="1440" height="680" fill="url(#blocks)" />

        {/* Lanes */}
        {[...LANE_H, ...LANE_V].map((d, i) => (
          <path key={`lnc-${i}`} d={d} stroke={C.laneCasing}   strokeWidth="3"   fill="none" />
        ))}
        {[...LANE_H, ...LANE_V].map((d, i) => (
          <path key={`lns-${i}`} d={d} stroke={C.laneSurface}  strokeWidth="1.8" fill="none" />
        ))}

        {/* Minor streets */}
        {[...MINOR_H, ...MINOR_V].map((d, i) => (
          <path key={`mnc-${i}`} d={d} stroke={C.minorCasing}   strokeWidth="5"   fill="none" />
        ))}
        {[...MINOR_H, ...MINOR_V].map((d, i) => (
          <path key={`mns-${i}`} d={d} stroke={C.minorSurface}  strokeWidth="3"   fill="none" />
        ))}
        {CURVED_MINOR.map((d, i) => (
          <path key={`cmc-${i}`} d={d} stroke={C.minorCasing}   strokeWidth="5"   fill="none" />
        ))}
        {CURVED_MINOR.map((d, i) => (
          <path key={`cms-${i}`} d={d} stroke={C.minorSurface}  strokeWidth="3"   fill="none" />
        ))}

        {/* Secondary roads */}
        {[...SECONDARY_H, ...SECONDARY_V].map((d, i) => (
          <path key={`sec-${i}`} d={d} stroke={C.secondaryCasing}  strokeWidth="8"   fill="none" />
        ))}
        {[...SECONDARY_H, ...SECONDARY_V].map((d, i) => (
          <path key={`ses-${i}`} d={d} stroke={C.secondarySurface} strokeWidth="5"   fill="none" />
        ))}
        {CURVED_SECONDARY.map((d, i) => (
          <path key={`csc-${i}`} d={d} stroke={C.secondaryCasing}  strokeWidth="8"   fill="none" />
        ))}
        {CURVED_SECONDARY.map((d, i) => (
          <path key={`css-${i}`} d={d} stroke={C.secondarySurface} strokeWidth="5"   fill="none" />
        ))}

        {/* Primary arteries */}
        {[...PRIMARY_H, ...PRIMARY_V].map((d, i) => (
          <path key={`prc-${i}`} d={d} stroke={C.primaryCasing}  strokeWidth="10" fill="none" />
        ))}
        {[...PRIMARY_H, ...PRIMARY_V].map((d, i) => (
          <path key={`prs-${i}`} d={d} stroke={C.primarySurface} strokeWidth="6"  fill="none" />
        ))}

        {/* Ping pulses */}
        {pings.map((p, i) => {
          const node = N[p.nodeIndex]
          const delay = `${(i * 0.85).toFixed(2)}s`
          return (
            <g key={`ping-node-${p.nodeIndex}`}>
              {/* Continuous ambient pulse */}
              <circle cx={node.x} cy={node.y} r="3.5" fill="none" stroke="#dc2626" strokeWidth="1.5" opacity="0.4">
                <animate attributeName="r"       values="3.5;22"   dur="2.5s" begin={delay} repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.65;0"   dur="2.5s" begin={delay} repeatCount="indefinite" />
              </circle>
              {/* Solid dot */}
              <circle cx={node.x} cy={node.y} r="3.5" fill="#dc2626" opacity="0.4" />
              {/* Arrival burst - fast outward ring */}
              {p.pulsing && (
                <g key={`burst-${p.pulseKey}`}>
                  <circle cx={node.x} cy={node.y} r="3.5" fill="none" stroke="#dc2626" strokeWidth="2.5">
                    <animate attributeName="r"       values="3.5;32"  dur="0.8s" begin="0s" fill="freeze" />
                    <animate attributeName="opacity" values="0.9;0"   dur="0.8s" begin="0s" fill="freeze" />
                  </circle>
                </g>
              )}
            </g>
          )
        })}

        {/* Cars - positioned via road path interpolation */}
        {cars.map(car => {
          const pos = positionOnPath(car.path, car.progress)
          return (
            <g key={car.id}>
              <circle cx={pos.x} cy={pos.y} r="5"   fill="#dc2626" opacity="0.3" />
              <circle cx={pos.x} cy={pos.y} r="2"   fill="white"   opacity="0.7"  />
            </g>
          )
        })}

        {/* Overlays */}
        <rect x="0" y="0" width="1440" height="680" fill="url(#centreFade)" />
        <rect x="0" y="0" width="1440" height="680" fill="url(#edgeFade)" />
      </svg>
    </div>
  )
}
